import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { TrendingUp, Zap, Target, BarChart3, Shield, Clock, Menu, X, ChevronDown } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage({ setAuth }) {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('optimexisai_token', response.data.token);
      localStorage.setItem('optimexisai_user', JSON.stringify(response.data.user));
      
      setAuth(true);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const scrollToPricing = () => {
    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <nav className="nav-bar">
          <div className="logo" data-testid="logo">
            <div className="pulse-icon"></div>
            <span>OptimexisAi</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="nav-actions desktop-nav">
            <Button
              variant="ghost"
              onClick={() => {
                setIsLogin(true);
                setShowAuthModal(true);
              }}
              data-testid="nav-login-btn"
            >
              Login
            </Button>
            <Button
              onClick={() => {
                setIsLogin(false);
                setShowAuthModal(true);
              }}
              data-testid="nav-signup-btn"
            >
              Start Free Trial
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            className="mobile-menu-btn"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            data-testid="mobile-menu-btn"
          >
            {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </nav>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="mobile-menu" data-testid="mobile-menu">
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => {
                setIsLogin(true);
                setShowAuthModal(true);
                setShowMobileMenu(false);
              }}
              data-testid="mobile-login-btn"
            >
              Login
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => {
                scrollToPricing();
                setShowMobileMenu(false);
              }}
            >
              Pricing
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => {
                document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' });
                setShowMobileMenu(false);
              }}
            >
              About
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => {
                document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                setShowMobileMenu(false);
              }}
            >
              Contact
            </Button>
            <Button
              className="w-full"
              onClick={() => {
                setIsLogin(false);
                setShowAuthModal(true);
                setShowMobileMenu(false);
              }}
            >
              Start Free Trial
            </Button>
          </div>
        )}

        <div className="hero-content">
          <h1 className="hero-title" data-testid="hero-title">
            Maximise Your Shopify Profit Automatically with OptimexisAi
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            OptimexisAi monitors your sales, stock, and competitors — then adjusts prices daily to boost your revenue while you sleep.
          </p>
          <div className="hero-cta">
            <Button
              size="lg"
              onClick={() => {
                setIsLogin(false);
                setShowAuthModal(true);
              }}
              data-testid="hero-cta-btn"
            >
              Start Free 7-Day Trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={scrollToPricing}
              data-testid="view-pricing-btn"
            >
              View Pricing
            </Button>
          </div>
          <p className="hero-note">No credit card required • Cancel anytime</p>
        </div>

        <div className="pulse-animation"></div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2 className="section-title" data-testid="features-title">Key Features</h2>
        <div className="features-grid">
          <Card className="feature-card" data-testid="feature-card-1">
            <TrendingUp className="feature-icon" />
            <h3>Smart Price Testing</h3>
            <p>Automatically test optimal prices for every SKU based on demand and competition</p>
          </Card>
          <Card className="feature-card" data-testid="feature-card-2">
            <Target className="feature-icon" />
            <h3>Competitor Tracking</h3>
            <p>Monitor competitor prices and stay competitive with real-time market insights</p>
          </Card>
          <Card className="feature-card" data-testid="feature-card-3">
            <Zap className="feature-icon" />
            <h3>Flash Sale Automation</h3>
            <p>One-click flash sales for slow-moving inventory with AI-powered clearance mode</p>
          </Card>
          <Card className="feature-card" data-testid="feature-card-4">
            <BarChart3 className="feature-icon" />
            <h3>Profit Growth Reports</h3>
            <p>Weekly automated reports showing revenue increases and optimization insights</p>
          </Card>
          <Card className="feature-card" data-testid="feature-card-5">
            <Shield className="feature-icon" />
            <h3>Price Protection</h3>
            <p>Set boundaries and approve changes manually or enable full automation</p>
          </Card>
          <Card className="feature-card" data-testid="feature-card-6">
            <Clock className="feature-icon" />
            <h3>24/7 Optimization</h3>
            <p>Continuous monitoring and daily price adjustments while you focus on growth</p>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-container">
          <h2 className="section-title" data-testid="faq-title">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="faq-accordion" data-testid="faq-accordion">
            <AccordionItem value="item-1" data-testid="faq-item-1">
              <AccordionTrigger>What exactly does OptimexisAi do?</AccordionTrigger>
              <AccordionContent>
                OptimexisAi is an AI-powered pricing optimization platform that automatically analyzes your store's sales data, stock levels, and competitor prices to recommend optimal pricing strategies. It monitors your products 24/7 and suggests price adjustments that maximize your profit while maintaining healthy sales velocity. You can choose to approve changes manually or enable full automation.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" data-testid="faq-item-2">
              <AccordionTrigger>How can it increase my profit?</AccordionTrigger>
              <AccordionContent>
                OptimexisAi uses advanced AI to identify pricing opportunities you might miss. It finds products you're undercharging for (where customers will pay more), detects slow-moving inventory that needs discounting, and spots high-demand items where you can raise prices. Our users typically see 12-20% profit increases within the first month by optimizing prices across their catalog.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" data-testid="faq-item-3">
              <AccordionTrigger>How does it connect to my store?</AccordionTrigger>
              <AccordionContent>
                OptimexisAi connects securely to your Shopify or WooCommerce store through their official APIs. During setup, you'll authorize OptimexisAi to access your product and sales data (read-only initially). You maintain full control and can revoke access anytime. We never access customer payment information or personal data—only product prices, inventory, and sales metrics.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" data-testid="faq-item-4">
              <AccordionTrigger>Can I control what it changes?</AccordionTrigger>
              <AccordionContent>
                Absolutely! You have complete control. Choose which products OptimexisAi monitors, set price boundaries (min/max limits), and decide your optimization mode (Profit Max, Volume Boost, or Clearance). You can review and approve each suggestion manually, or enable auto-apply for trusted products. You can pause or stop optimization anytime with one click.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" data-testid="faq-item-5">
              <AccordionTrigger>Do I need any technical skills?</AccordionTrigger>
              <AccordionContent>
                Not at all! OptimexisAi is designed for store owners, not developers. The setup takes less than 5 minutes—just connect your store and select which products to monitor. Our dashboard is intuitive and shows clear recommendations with plain-English explanations. If you can manage your Shopify or WooCommerce store, you can use OptimexisAi. Plus, our support team is here to help anytime.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" data-testid="faq-item-6">
              <AccordionTrigger>How soon will I see results?</AccordionTrigger>
              <AccordionContent>
                Most users see their first pricing recommendations within 24 hours of connecting their store. Measurable profit improvements typically appear within 7-14 days as optimized prices take effect and sales data accumulates. Our AI learns from your specific store data, so results improve over time. The 7-day free trial gives you enough time to see real results before committing.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" data-testid="faq-item-7">
              <AccordionTrigger>Is my data safe?</AccordionTrigger>
              <AccordionContent>
                Your data security is our top priority. We use bank-level encryption (256-bit SSL) for all data transmission and storage. We're fully GDPR compliant and never sell or share your data with third parties. Our infrastructure is hosted on secure, SOC 2 certified servers. We only access the minimum data needed for pricing optimization—never your customer payment details or personal information.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" data-testid="faq-item-8">
              <AccordionTrigger>What makes OptimexisAi different from other pricing tools?</AccordionTrigger>
              <AccordionContent>
                Unlike basic repricing tools that only match competitors, OptimexisAi uses advanced AI (GPT-5) to understand your entire business context—demand patterns, inventory levels, seasonality, and profit margins. We optimize for YOUR profit, not just sales volume. Plus, we offer three optimization modes (Profit Max, Volume Boost, Clearance) so you can adapt to different business goals. Most competitors offer one-size-fits-all pricing.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="faq-cta" data-testid="faq-cta">
            <div className="faq-cta-content">
              <p className="faq-cta-text">
                💬 <strong>Still unsure?</strong> Join our free beta — let OptimexisAi run one week of tests on your store and see your results before paying a penny.
              </p>
              <Button
                size="lg"
                onClick={() => {
                  setIsLogin(false);
                  setShowAuthModal(true);
                }}
                data-testid="faq-cta-btn"
              >
                Start Free 7-Day Trial
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* About Section */}
      <section className="about-section" id="about-section">
        <div className="about-container">
          <h2 className="section-title" data-testid="about-title">About OptimexisAi</h2>
          <div className="about-content">
            <div className="about-text">
              <p className="about-description">
                OptimexisAi is an AI-powered pricing optimization platform designed to help e-commerce store owners maximize their profits automatically. Built by a team of data scientists and e-commerce experts, our platform uses advanced machine learning algorithms to analyze your sales data, monitor competitors, and recommend optimal pricing strategies.
              </p>
              <p className="about-description">
                We believe that pricing shouldn't be guesswork. With OptimexisAi, you get data-driven insights that help you make smarter pricing decisions, increase conversion rates, and boost your bottom line—all while you focus on growing your business.
              </p>
              <div className="about-stats">
                <div className="stat-item">
                  <h3>10,000+</h3>
                  <p>Active Stores</p>
                </div>
                <div className="stat-item">
                  <h3>£50M+</h3>
                  <p>Revenue Optimized</p>
                </div>
                <div className="stat-item">
                  <h3>18%</h3>
                  <p>Avg. Profit Increase</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing-section">
        <h2 className="section-title" data-testid="pricing-title">Simple, Transparent Pricing</h2>
        <p className="pricing-subtitle">Start with a 7-day free trial • No credit card required</p>
        
        <div className="pricing-grid">
          <Card className="pricing-card" data-testid="pricing-card-starter">
            <div className="pricing-header">
              <h3>Starter</h3>
              <div className="price">
                <span className="currency">£</span>
                <span className="amount">49</span>
                <span className="period">/month</span>
              </div>
            </div>
            <ul className="pricing-features">
              <li>✓ 1 store connection</li>
              <li>✓ Monitor 5 SKUs</li>
              <li>✓ Manual approval required</li>
              <li>✓ Basic analytics</li>
              <li>✓ Email support</li>
            </ul>
            <Button
              className="pricing-btn"
              variant="outline"
              onClick={() => {
                setIsLogin(false);
                setShowAuthModal(true);
              }}
              data-testid="starter-plan-btn"
            >
              Start Free Trial
            </Button>
          </Card>

          <Card className="pricing-card featured" data-testid="pricing-card-pro">
            <div className="featured-badge">Most Popular</div>
            <div className="pricing-header">
              <h3>Pro</h3>
              <div className="price">
                <span className="currency">£</span>
                <span className="amount">149</span>
                <span className="period">/month</span>
              </div>
            </div>
            <ul className="pricing-features">
              <li>✓ 1 store connection</li>
              <li>✓ Monitor 10 SKUs</li>
              <li>✓ Competitor tracking</li>
              <li>✓ Advanced analytics</li>
              <li>✓ Weekly reports</li>
              <li>✓ Priority support</li>
            </ul>
            <Button
              className="pricing-btn"
              onClick={() => {
                setIsLogin(false);
                setShowAuthModal(true);
              }}
              data-testid="pro-plan-btn"
            >
              Start Free Trial
            </Button>
          </Card>

          <Card className="pricing-card" data-testid="pricing-card-growth">
            <div className="pricing-header">
              <h3>Growth</h3>
              <div className="price">
                <span className="currency">£</span>
                <span className="amount">299</span>
                <span className="period">/month</span>
              </div>
            </div>
            <ul className="pricing-features">
              <li>✓ Unlimited stores</li>
              <li>✓ Unlimited SKUs</li>
              <li>✓ Auto-apply pricing</li>
              <li>✓ Competitor tracking</li>
              <li>✓ Custom reports</li>
              <li>✓ Dedicated support</li>
            </ul>
            <Button
              className="pricing-btn"
              variant="outline"
              onClick={() => {
                setIsLogin(false);
                setShowAuthModal(true);
              }}
              data-testid="growth-plan-btn"
            >
              Start Free Trial
            </Button>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" id="contact-section">
        <div className="contact-container">
          <h2 className="section-title" data-testid="contact-title">Get In Touch</h2>
          <p className="contact-subtitle">Have questions? We'd love to hear from you.</p>
          
          <div className="contact-content">
            <Card className="contact-card">
              <h3>Contact Information</h3>
              <div className="contact-info">
                <div className="contact-item">
                  <strong>Email:</strong>
                  <a href="mailto:hello@optimexisai.com">hello@optimexisai.com</a>
                </div>
                <div className="contact-item">
                  <strong>Sales:</strong>
                  <a href="mailto:sales@optimexisai.com">sales@optimexisai.com</a>
                </div>
                <div className="contact-item">
                  <strong>Support Hours:</strong>
                  <span>Monday - Friday, 9:00 AM - 6:00 PM GMT</span>
                </div>
                <div className="contact-item">
                  <strong>Response Time:</strong>
                  <span>Within 24 hours</span>
                </div>
              </div>
            </Card>

            <Card className="contact-card">
              <h3>Quick Links</h3>
              <div className="quick-links">
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    setIsLogin(false);
                    setShowAuthModal(true);
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    setIsLogin(true);
                    setShowAuthModal(true);
                  }}
                >
                  Login to Dashboard
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={scrollToPricing}
                >
                  View Pricing Plans
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            <div className="pulse-icon"></div>
            <span>OptimexisAi</span>
          </div>
          <p className="footer-tagline">Optimize smarter, profit faster with AI-driven pricing.</p>
          <p className="footer-copyright">© 2025 OptimexisAi. All rights reserved.</p>
        </div>
      </footer>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent data-testid="auth-modal">
          <DialogHeader>
            <DialogTitle data-testid="auth-modal-title">
              {isLogin ? 'Welcome Back' : 'Start Your Free Trial'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAuth} className="auth-form">
            {!isLogin && (
              <div className="form-group">
                <label>Full Name</label>
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="auth-fullname-input"
                />
              </div>
            )}
            <div className="form-group">
              <label>Email</label>
              <Input
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="auth-email-input"
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <Input
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="auth-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="auth-submit-btn">
              {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
            </Button>
            <p className="auth-toggle">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <span onClick={() => setIsLogin(!isLogin)} data-testid="auth-toggle-link">
                {isLogin ? 'Sign up' : 'Login'}
              </span>
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}